import type { FC } from 'react';
import {
  alpha,
  Box,
  Button,
  Card,
  CardHeader,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { PliantTransaction } from '@pflegenavi/frontend/api-nursing-home';
import { usePliantTransactions } from '@pflegenavi/frontend/api-nursing-home';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import {
  useEmployeeIsNursingHomeAdminOrManager,
  useNursingHomeContext,
  useSelectedEmployeeIds,
} from '@pflegenavi/frontend/nursing-home-context';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { FeatureFlag } from '@pflegenavi/shared/api';
import {
  TIME_FORMAT,
  YEAR_SHORTMONTH_DAY_FORMAT,
} from '@pflegenavi/shared/constants';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { Iconify, TableNoData } from '@pflegenavi/web-components';
import AllSetSvg from '../assets/all_set.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const MAX_ROWS = 5;

const CATEGORY_ICON: Record<PliantTransaction['pliant_category'], string> = {
  ADVERTISING_AND_MARKETING: 'mdi:bullhorn-outline',
  COMPUTING_AND_SOFTWARE: 'mdi:laptop',
  EDUCATION_AND_TRAINING: 'mdi:school-outline',
  ELECTRONICS_AND_IT_EQUIPMENT: 'mdi:monitor',
  ENTERTAINMENT_AND_WELLNESS: 'mdi:party-popper',
  FOOD_AND_DRINKS: 'mdi:silverware-fork-knife',
  GIFTS_AND_VOUCHERS: 'mdi:gift-outline',
  MATERIALS_AND_PACKAGING: 'mdi:package-variant-closed',
  OFFICE_SUPPLIES_AND_EQUIPMENT: 'mdi:pencil-ruler',
  SERVICES: 'mdi:room-service-outline',
  TRAVEL_AND_ACCOMMODATION: 'mdi:airplane',
  HEALTHCARE: 'mdi:medical-bag',
  OTHER: 'mdi:tag-outline',
};

const cardholderName = (transaction: PliantTransaction): string => {
  const employee = transaction.pliant_cardholder?.employee;
  return (
    `${employee?.first_name ?? ''} ${employee?.last_name ?? ''}`.trim() || '--'
  );
};

export const CardPaymentsCard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fDate = useFormatDate();
  const { fCurrencyCents } = useFormatting();
  const { selectedNursingHome } = useNursingHomeContext();
  const isAdminOrManager = useEmployeeIsNursingHomeAdminOrManager();
  // undefined -> all payments (admin/manager); [userId] -> own only (regular employee).
  const employeeIds = useSelectedEmployeeIds();

  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );

  // API always sorts created_at desc, so this yields the latest payments.
  const { data } = usePliantTransactions(
    {
      params: {
        page: 1,
        page_size: MAX_ROWS,
        legal_entity_id: selectedNursingHome?.legalEntityId ?? '',
        nursing_home_id: selectedNursingHome?.id ?? '',
        employee_ids: employeeIds,
      },
    },
    {
      enabled: expenseManagementEnabled && !!selectedNursingHome?.legalEntityId,
    }
  );

  if (!expenseManagementEnabled || !data) {
    return null;
  }

  const transactions = data.data ?? [];

  return (
    <Card
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: {
          xs: '100%',
          md: 420,
        },
      }}
      data-cy="dashboard-card-payments"
    >
      <CardHeader
        title={t('dashboard.card-payments.title')}
        sx={{ mb: 3 }}
        titleTypographyProps={{
          sx: { fontWeight: 600 },
        }}
      />
      <TableContainer
        sx={{
          maxWidth: '96%',
          marginLeft: 'auto',
          marginRight: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                {t('dashboard.card-payments.table.merchant')}
              </TableCell>
              <TableCell>{t('dashboard.card-payments.table.date')}</TableCell>
              <TableCell align="right">
                {t('dashboard.card-payments.table.amount')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 && (
              <TableNoData colSpan={3} isNotFound>
                <img
                  alt="empty content"
                  src={AllSetSvg}
                  height={170}
                  width={140}
                />
                <Typography
                  variant="inherit"
                  fontWeight="700"
                  textAlign="center"
                >
                  {t('dashboard.card-payments.no-data')}
                </Typography>
              </TableNoData>
            )}
            {transactions.map((transaction) => {
              const date =
                transaction.booked_at ??
                transaction.authorized_at ??
                transaction.created_at;
              return (
                <TableRow
                  key={transaction.pliant_transaction_id}
                  data-cy="dashboard-card-payment-row"
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2.5}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                        }}
                      >
                        <Iconify
                          icon={
                            CATEGORY_ICON[transaction.pliant_category] ??
                            CATEGORY_ICON.OTHER
                          }
                          width={20}
                          height={20}
                        />
                      </Box>
                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{ fontWeight: 700 }}
                        >
                          {transaction.merchant_name || '-'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {cardholderName(transaction)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {fDate(new Date(date), {
                        ...TIME_FORMAT,
                        ...YEAR_SHORTMONTH_DAY_FORMAT,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" noWrap>
                      {fCurrencyCents(transaction.billing_amount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          p: 2,
          textAlign: 'right',
          mt: 'auto',
        }}
      >
        <Button
          data-cy="dashboard-card-payments-view-all-button"
          size="small"
          color="inherit"
          endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
          onClick={() =>
            navigate(
              isAdminOrManager
                ? nhAppMainPages.NURSING_HOME_ACCOUNTING_CARD_PAYMENTS
                : nhAppMainPages.TRANSACTIONS_UNASSOCIATED_CARD_PAYMENTS
            )
          }
        >
          {t('dashboard.label.viewAll')}
        </Button>
      </Box>
    </Card>
  );
};
